/**
 * Api.ts
 * In this Component All fetch methods like GET,POST,PATCH are defined commonly so we can use ApiUtil class throughout the Project.
 */
import Utils from ".";
import { alert } from "../components/toast-container/alert";
import Aes from "./aes.encrypt.decrypt";
import AuthUtil from "./auth";
import { __prod__, SESSION_TIME_OUT, UNAUTHORIZEDREQUEST } from "./constants";
import EncryptUtils from "./encrypt";
import { StoreUtil } from "./store";
// Base URL for API requests
const BASEURL = process.env.REACT_APP_PUBLIC_API_BASEURL || "/";
// Token keys
const REACT_APP_PUBLIC_ACCESS_TOKEN_KEY =
  process.env.REACT_APP_PUBLIC_ACCESS_TOKEN_KEY || "";
const REACT_APP_PUBLIC_REFRESH_TOKEN_KEY =
  process.env.REACT_APP_PUBLIC_REFRESH_TOKEN_KEY || "";
if (typeof window !== "undefined") {
  // Get the site's base URL from the window location
  const REACT_APP_PUBLIC_SITE_BASEURL = window.location.origin || "/";
}

const SESSION_TIME: any = process.env.REACT_APP_PUBLIC_SESSION_TIME;
// Default headers for API requests
const DEFAULT_HEADERS: RequestInit = {
  method: "GET",
  mode: "cors",
  cache: "no-cache",
  credentials: "same-origin",
  headers: {
    "Content-Type": "application/json",
  },
  redirect: "follow",
  referrer: "no-referrer",
};
// Class definition for ApiUtil
class ApiUtil {
  /**
   * Get an instance of the ApiUtil class
   */
  static getClientInstance() {
    return new ApiUtil();
  }

  private secureEndPoints = [
    "/UserManagement/ChangePassword",
    "/UserManagement/GenerateOtp",
    "/UserManagement/ValidateOtp",
    "/OpenPage/ChangePassword",
    "/OpenPage/GenerateOtp",
    "/OpenPage/ValidateOtp",
    "/Auth/SignIn",
  ];

  private abortControllers: Array<AbortController> = [];

  /**
   * Validate the request data
   */
  // private validateRequest(data: any) {
  //   if (data.constructor.name == "FormData") return data;
  //   else return JSON.stringify(data);
  // }

  private completeLogoutFormalities() {
    StoreUtil.removeCookie(REACT_APP_PUBLIC_ACCESS_TOKEN_KEY);
    StoreUtil.removeCookie(REACT_APP_PUBLIC_REFRESH_TOKEN_KEY);
    StoreUtil.removeAllCookies();
    Utils.sleep(1500);

    // reload to site main page
    Utils.redirectUrl("/login");
  }

  private validateRequest(requestData: any) {
    if (requestData.constructor.name === "FormData") return requestData;
    else {
      if (__prod__) {
        requestData = JSON.stringify(requestData);
        requestData = EncryptUtils.encrypt(requestData);
        return JSON.stringify(requestData);
      } else {
        return JSON.stringify(requestData);
      }
    }
  }

  /**
   * Validate the API response
   */
  private async validateResponse(response: Response, url: string) {
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      if (__prod__ && this.secureEndPoints.includes(url)) {
        const encryptedResponse = await response.json();
        const decryptedResponse: any = EncryptUtils.decrypt(encryptedResponse);
        const token = decryptedResponse?.data?.authToken;
        if (token) AuthUtil.setToken(token);
        return JSON.parse(decryptedResponse);
      }

      const data = await response.json();
      const token = data?.data?.authToken;
      if (token) AuthUtil.setToken(token);
      return await data;
    } else if (contentType?.includes("application/octet-stream")) {
      return await response.blob();
    }
  }

  // private async validateResponse(response: Response) {
  //   const contentType = response.headers.get("content-type");
  //   if (contentType?.includes("application/json")) {
  //     if (__prod__) {
  //       const encryptedResponse = await response.json();
  //       const decryptedResponse: any = EncryptUtils.decrypt(encryptedResponse);
  //       return JSON.parse(decryptedResponse);
  //     } else {
  //       return await response.json();
  //     }
  //   } else if (contentType?.includes("application/octet-stream")) {
  //     return await response.blob();
  //   }
  // }
  /**
   * Common function for making API requests
   */
  private async apiFetch(
    url: string,
    requestInit: RequestInit = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
    requestData: object | undefined = undefined
  ): Promise<any> {
    try {
      if (!url) throw "No url provided";
      if (this.isTokenExpired() && AuthUtil.isTokenExist()) {
        await alert({
          title: "Session Timeout",
          type: "error",
          description: "Session timed out, please login again!",
        });

        AuthUtil.logout();
        Utils.redirectUrl(`/login`);
      } else {
        AuthUtil.isTokenExist() && apiUtil.setTime();
        const init = {
          ...DEFAULT_HEADERS,
          ...requestInit,
          ...(requestData ? { body: this.validateRequest(requestData) } : {}),
        };
        init.headers = {
          ...(init.headers ?? {}),
          ...AuthUtil.getToken(),
        };
        const response = await fetch(`${BASEURL}${url}`, init);
        if (response.status >= 200 && response.status <= 299) {
          return await this.validateResponse(response, url);
        } else {
          if (response.status === 403 || response.status === 401) {
            if (response.status === 401) {
              await alert({
                title: "Session Timeout",
                type: "error",
                description: "Session timed out, please login again!",
              });
              this.completeLogoutFormalities();
            }
            if (response.status === 403) {
              await alert({
                title: "Unauthorized",
                type: "error",
                description: UNAUTHORIZEDREQUEST,
              });
              this.completeLogoutFormalities();
            }
          }
          throw new Error(`${response.status} ${response.statusText}`);
        }
      }
    } catch (error) {
      this.errorHandler(error);
    }
  }
  /**
   * Make a GET request
   */
  public get = async (url: string, data = {}, headers = {}): Promise<any> => {
    return await this.apiFetch(`${url}${Utils.getQueryString(data)}`, {
      ...(headers ? { headers } : {}),
      method: "GET",
    });
  };
  /**
   * Make a POST request
   */
  public post = async (url: string, data = {}, headers?: any): Promise<any> => {
    return await this.apiFetch(
      url,
      {
        ...(headers ? { headers } : {}),
        method: "POST",
      },
      data
    );
  };
  /**
   * Make a patch request
   */
  public async patch(url: string, data = {}, headers?: any): Promise<any> {
    return await this.apiFetch(
      // `${url}${Utils.getQueryString(data)}`,
      `${url}`,
      {
        ...(headers ? { headers } : {}),
        method: "PATCH",
      },
      data
    );
  }
  /**
   * Make a delete request
   */
  public async delete(url: string, data = {}, headers?: any): Promise<any> {
    return await this.apiFetch(
      `${url}${Utils.getQueryString(data)}`,
      {
        ...(headers ? { headers } : {}),
        method: "DELETE",
      },
      data
    );
  }
  // Handle API request errors
  public errorHandler(error: any) {
    console.error(error);
  }
  // Abort all ongoing API requests
  public abortAllRequests = () => {
    for (let controller of this.abortControllers) {
      controller.abort();
    }
    this.abortControllers = [];
  };
  // Handler for aborted requests
  public abortRequestHandler = (abort: any) => {};

  public setTime() {
    StoreUtil.setCookie(
      SESSION_TIME_OUT,
      Aes.encryptURLSafe(new Date().getTime().toString())
    );
  }

  static getTime() {
    let encSession = StoreUtil.getCookie(SESSION_TIME_OUT);
    if (encSession) {
      encSession = Aes.decryptURLSafe(encSession);
      return parseInt(encSession);
    } else {
      return false;
    }
  }

  // To check session time out
  public isTokenExpired() {
    if (ApiUtil.getTime() === false) {
      return true;
    } else {
      const getTime: any = ApiUtil.getTime();
      const timeDifference = new Date().getTime() - getTime;
      // 30 Seconds Condition: 1 sec = 1000 miliseconds for testing only *****
      return timeDifference > SESSION_TIME * 1000 ? true : false;
    }
  }
}
// Singleton instance of ApiUtil
const apiUtil: ApiUtil = ApiUtil.getClientInstance();
export { apiUtil as default };
